<script setup>
/**
 * Imports
 */
import { IconComponent } from "@/components/ui";
import { CarNumberComponent, TagComponent } from "@/components/features";
import { CarCardTagsComponent } from "./index";
import { computed } from "vue";
import { mappingStyles } from "@/modules/autopark/enums/transport/status.enum";
import { usePermissions } from "@/modules/auth/composables/usePermissions";
import { formatNumber } from "@/shared/utils";

/**
 * Props & Emits
 */
const props = defineProps({
  data: Object,
  disableRouting: {
    type: Boolean,
    default: false,
  },
  active: {
    type: Boolean,
    default: false,
  },
  selected: {
    type: Boolean,
    default: false,
  },
  // Занятость отсеков на дату доставки: [{ id, number, capacity, planned_volume, free_volume, occupied_by }]
  availability: {
    type: Array,
    default: null,
  },
  availabilityLoading: {
    type: Boolean,
    default: false,
  },
});

/**
 * Variables
 */
const { hasAccess } = usePermissions();

/**
 * Computed
 */
const statusTag = computed(() => mappingStyles[props.data?.status] || {});
const canView = computed(() => hasAccess("transport:show"));
// Водитель необязателен: бэкенд отдаёт driver=null, если ТС без водителя либо у пользователя
// нет driver_info; телефон в базе тоже часто пустой. Плюс DriverResource умеет вернуть пустой
// объект/массив (truthy) — поэтому ориентируемся на содержимое полей, а не на сам driver.
const driverName = computed(() => {
  const driver = props.data?.driver;
  if (!driver) return "";

  const name = [driver.first_name, driver.last_name].filter(Boolean).join(" ");
  return name || driver.full_name || "";
});
const driverPhone = computed(() => props.data?.driver?.phone || "");
const hasDriver = computed(() => Boolean(driverName.value || driverPhone.value));
const volumeUnit = computed(() => {
  return props.data?.fuel_type === "gas" ? "кг" : "л";
});
const hasAvailability = computed(() => Array.isArray(props.availability) && props.availability.length > 0);
const showCompartments = computed(() => {
  return (props.data?.compartments_count || 0) > 1 || hasAvailability.value;
});

// Отсеки для вывода: при наличии данных занятости показываем «свободно X из Y», иначе паспортную ёмкость
const compartmentRows = computed(() => {
  if (hasAvailability.value) {
    return props.availability.map((item) => ({
      number: item.number,
      capacity: Number(item.capacity ?? 0),
      free: Number(item.free_volume ?? item.capacity ?? 0),
      withAvailability: true,
    }));
  }
  return (props.data?.compartments || []).map((item) => ({
    number: item.number,
    capacity: Number(item.capacity ?? item.volume ?? 0),
    free: null,
    withAvailability: false,
  }));
});

// Полностью свободные отсеки (без планов других заявок на дату)
const freeCompartmentsCount = computed(() =>
  hasAvailability.value
    ? props.availability.filter((item) => Number(item.planned_volume || 0) === 0).length
    : null
);

/**
 * Methods
 */

/**
 * Hooks, Watchers
 */
</script>

<template>
  <div
    v-if="data"
    class="car-card"
    :class="{
      'car-card--active': active,
      'car-card--selected': selected,
      'car-card--disable-routing': disableRouting,
    }"
  >
    <div class="car-card__header">
      <span class="car-card__name">{{ data.model }}, {{ data.year_of_production }}</span>
      <IconComponent v-if="active" class="car-card__check" name="circle-check" />
    </div>
    <CarNumberComponent :data="data" />
    <TagComponent class="car-card__status" size="lg" :color="statusTag.color" :icon="statusTag.icon">
      <span>{{ statusTag.title }}</span>
    </TagComponent>

    <div v-if="hasDriver" class="car-card__driver">
      <span v-if="driverName" class="car-card__driver-name">
        <IconComponent name="user" size="xs" />
        <span>{{ driverName }}</span>
      </span>
      <span v-if="driverPhone" class="car-card__driver-phone">
        <IconComponent name="phone" size="xs" />
        <span>{{ driverPhone }}</span>
      </span>
    </div>

    <CarCardTagsComponent :data="data" />

    <div class="car-card__volume">
      <div v-if="data.type" class="car-card__type">
        <TagComponent size="sm">
          <span>{{ data.type }}</span>
        </TagComponent>
      </div>

      <div class="car-card__volume-row">
        <span class="car-card__volume-label">Общий объем:</span>
        <span class="car-card__volume-value">
          {{ formatNumber(data?.compartments_sum) }} {{ volumeUnit }}
        </span>
      </div>

      <div v-if="freeCompartmentsCount !== null" class="car-card__free">
        <TagComponent size="sm" :color="freeCompartmentsCount ? 'green-light' : 'red-light'">
          <span>Свободно отсеков: {{ freeCompartmentsCount }}</span>
        </TagComponent>
      </div>
      <div v-else-if="availabilityLoading" class="car-card__free car-card__free--loading">
        <span>Проверяем занятость отсеков...</span>
      </div>

      <div v-if="showCompartments" class="car-card__compartments-inline">
        <div
          v-for="compartment in compartmentRows"
          :key="compartment.number"
          class="car-card__compartment-inline"
          :class="{ 'car-card__compartment-inline--busy': compartment.withAvailability && !compartment.free }"
        >
          <span class="car-card__compartment-number">Отсек {{ compartment.number }}:</span>
          <span class="car-card__compartment-volume">
            <template v-if="compartment.withAvailability">
              свободно {{ formatNumber(compartment.free) }} из {{ formatNumber(compartment.capacity) }}
              {{ volumeUnit }}
            </template>
            <template v-else>{{ formatNumber(compartment.capacity) }} {{ volumeUnit }}</template>
          </span>
        </div>
      </div>
    </div>
    <router-link
      v-if="!disableRouting && canView"
      :to="{ name: 'transport', params: { id: data.id } }"
      class="car-card__link"
    />
  </div>
</template>

<style lang="stylus">
.car-card {
  --car-card-border-width: 1px;
  --car-card-padding: 20px;
  --car-card-border-radius: 12px;
  --car-card-width: 264px

	position: relative
	padding: calc(var(--car-card-padding) - var(--car-card-border-width))
	background: var(--white)
	border-radius: var(--car-card-border-radius)
	display: flex;
	flex-direction: column;
	gap: 10px
  border: var(--car-card-border-width) solid transparent
  cursor: pointer
  width 100%
  max-width var(--car-card-width)
  transition: var(--transition)

  &::after {
    content: '';
    display: block
    position: absolute;
    inset calc(-1 * var(--car-card-border-width))
    border: 3px solid transparent
    border-radius: var(--car-card-border-radius)
    transition: var(--transition)
    pointer-events none
  }

  &:not(&--active):not(&--selected):hover {
    border: 1px solid var(--gray, #8F95A2);
    box-shadow: 5px 5px 10px 0 var(--gray-4-o3)
  }

  &--active::after {
    border-color var(--blue)
  }

  &--selected {
    cursor: default;
  }

  &--selected {
    border-color var(--blue, #3C68C8);
    box-shadow: 5px 5px 10px 0 var(--black-o14);
  }

  &__link {
    position absolute
    inset 0
    z-index 0
  }

	&__header {
		display flex
		align-items flex-start
		gap: 10px
	}

	&__name {
		flex-grow 1
		font-size: 1.125rem
		font-weight: 500;
		line-height: normal;
	}

  &__check {
    color: var(--blue)
  }

  &__status {
    align-self flex-start
  }

  &__driver {
    display flex
    flex-direction column
    gap: 4px

    &-name, &-phone {
      display flex
      align-items center
      gap: 6px
      font-size: 0.875rem
      line-height: 1.4

      .icon {
        width 16px
        height 16px
        flex-shrink 0
      }
    }

    &-name {
      color: var(--blue, #3C68C8)
    }

    &-phone {
      color: var(--black)
    }
  }

	&__tags {
		display flex
		align-items center
		gap: 10px
    z-index: 1
	}

	&__tag {
		display flex
		align-items center
		gap: 6px
		font-size: 0.875rem
		line-height: normal;

		&--green {
			color: var(--green-2)
		}

    &--red {
      color: var(--red)
    }

		&--blue {
			color: var(--blue)
		}
	}

	&__img {
		height: 112px
		border-radius: 12px

    img {
      object-position: right
    }
	}

  &__free {
    display flex
    margin-top 6px

    &--loading {
      font-size 0.75rem
      color var(--gray)
    }
  }

  &__compartment-inline {
    &--busy {
      color var(--red)
    }
  }
}
</style>
